/**
 * Ingest Service - Client-side runner for contracts intelligence ingest
 *
 * This service orchestrates ingest via Firebase Cloud Functions:
 * 1. Calls the runIngest Cloud Function
 * 2. The function fetches from SAM.gov and stores in Firestore
 * 3. Returns results to the UI
 *
 * For demo mode, generates mock data locally.
 */
import {
  collection,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../../config/firebase";
import { getSamConfig } from "./samConfigService";
import {
  generateMockOpportunities,
  generateMockHistoricalAwards,
} from "../../../services/sam/mockData";
import {
  computeSimilarityScore,
  computeSimilarityBand,
} from "../../../services/fpds/similarityEngine";

// Initialize Firebase Functions
const functions = getFunctions();

/**
 * Delete all demo/mock data from Firestore via Cloud Function
 *
 * @param {Object} options
 * @param {Function} options.onProgress - Progress callback
 * @param {Function} options.onLog - Log callback
 * @returns {Promise<Object>} Deletion results
 */
export async function deleteDemoData({ onProgress, onLog } = {}) {
  const logger = {
    info: (msg) => {
      console.log("[Delete Demo]", msg);
      onLog?.({ level: "info", message: msg });
    },
    error: (msg) => {
      console.error("[Delete Demo]", msg);
      onLog?.({ level: "error", message: msg });
    },
  };

  try {
    logger.info("Deleting demo/mock data...");
    onProgress?.({ status: "starting", message: "Calling Cloud Function to delete demo data..." });

    // Call the deleteDemoData Cloud Function
    const deleteDemoDataFn = httpsCallable(functions, "deleteDemoData");

    onProgress?.({ status: "running", message: "Deleting mock opportunities and awards..." });

    const result = await deleteDemoDataFn({});
    const data = result.data;

    logger.info(`Demo data deleted: ${data.opportunities_deleted} opportunities, ${data.awards_deleted} awards`);
    onProgress?.({
      status: "completed",
      message: `Deleted ${data.opportunities_deleted} mock opportunities, ${data.awards_deleted} mock awards, ${data.runs_deleted} demo runs.`,
    });

    return data;
  } catch (error) {
    logger.error(`Delete failed: ${error.message}`);
    onProgress?.({
      status: "failed",
      message: `Failed to delete demo data: ${error.message}`,
    });
    throw error;
  }
}

/**
 * Run the full contracts intelligence ingest via Cloud Function
 *
 * @param {Object} options
 * @param {Function} options.onProgress - Progress callback
 * @param {Function} options.onLog - Log callback
 * @returns {Promise<Object>} Ingest results
 */
export async function runIngest({ onProgress, onLog } = {}) {
  const logger = {
    info: (msg) => {
      console.log("[Ingest]", msg);
      onLog?.({ level: "info", message: msg });
    },
    warn: (msg) => {
      console.warn("[Ingest]", msg);
      onLog?.({ level: "warn", message: msg });
    },
    error: (msg) => {
      console.error("[Ingest]", msg);
      onLog?.({ level: "error", message: msg });
    },
  };

  try {
    logger.info("Starting contracts intelligence ingest...");
    onProgress?.({ status: "starting", message: "Calling Cloud Function..." });

    // Call the runIngest Cloud Function
    const runIngestFn = httpsCallable(functions, "runIngest");

    onProgress?.({ status: "running", message: "Fetching opportunities from SAM.gov..." });

    const result = await runIngestFn({ mode: "full" });
    const data = result.data;

    logger.info(`Ingest completed: ${data.opportunities_stored} opportunities stored`);
    onProgress?.({
      status: "completed",
      message: `Completed! Stored ${data.opportunities_stored} opportunities.`,
    });

    return data;
  } catch (error) {
    logger.error(`Ingest failed: ${error.message}`);
    onProgress?.({
      status: "failed",
      message: `Failed: ${error.message}`,
    });
    throw error;
  }
}

/**
 * Run a test fetch via Cloud Function (limited results)
 */
export async function runTestIngest({ onProgress, onLog } = {}) {
  const logger = {
    info: (msg) => {
      console.log("[Test Ingest]", msg);
      onLog?.({ level: "info", message: msg });
    },
    warn: (msg) => {
      console.warn("[Test Ingest]", msg);
      onLog?.({ level: "warn", message: msg });
    },
    error: (msg) => {
      console.error("[Test Ingest]", msg);
      onLog?.({ level: "error", message: msg });
    },
  };

  try {
    logger.info("Starting test ingest...");
    onProgress?.({ status: "starting", message: "Running test ingest via Cloud Function..." });

    // Call the runIngest Cloud Function in test mode
    const runIngestFn = httpsCallable(functions, "runIngest");

    onProgress?.({ status: "running", message: "Testing SAM.gov API connection..." });

    const result = await runIngestFn({ mode: "test" });
    const data = result.data;

    logger.info(`Test completed: ${data.opportunities_stored} opportunities`);
    onProgress?.({
      status: "completed",
      message: `Test completed! Found ${data.opportunities_fetched} opportunities, stored ${data.opportunities_stored}.`,
    });

    return data;
  } catch (error) {
    logger.error(`Test failed: ${error.message}`);
    onProgress?.({
      status: "failed",
      message: `Test failed: ${error.message}`,
    });
    throw error;
  }
}

/**
 * Run demo ingest with mock data
 *
 * Use this to test the UI when you don't have a valid SAM.gov API key.
 * Generates realistic sample opportunities and stores them in Firestore.
 */
export async function runDemoIngest({ onProgress, onLog, count = 25 } = {}) {
  const logger = {
    info: (msg) => {
      console.log("[Demo Ingest]", msg);
      onLog?.({ level: "info", message: msg });
    },
    warn: (msg) => {
      console.warn("[Demo Ingest]", msg);
      onLog?.({ level: "warn", message: msg });
    },
    error: (msg) => {
      console.error("[Demo Ingest]", msg);
      onLog?.({ level: "error", message: msg });
    },
    debug: (msg) => {
      console.debug("[Demo Ingest]", msg);
    },
  };

  const results = {
    started_at: new Date(),
    status: "running",
    opportunities_fetched: 0,
    opportunities_stored: 0,
    similarity_matches: 0,
    errors: [],
  };

  try {
    logger.info("Starting DEMO ingest with mock data...");
    onProgress?.({ status: "starting", message: "Generating mock data..." });

    // Get configured NAICS codes
    const config = await getSamConfig();
    const naicsCodes = config.naics_codes?.length > 0
      ? config.naics_codes
      : ["541512", "541519", "541611"]; // Default codes if none configured

    logger.info(`Using ${naicsCodes.length} NAICS codes`);

    // Generate mock opportunities
    onProgress?.({ status: "running", message: "Generating mock opportunities..." });
    const opportunities = generateMockOpportunities(naicsCodes, count);
    results.opportunities_fetched = opportunities.length;
    logger.info(`Generated ${opportunities.length} mock opportunities`);

    // Generate mock historical awards for similarity
    onProgress?.({ status: "running", message: "Generating mock historical awards..." });
    const historicalAwards = generateMockHistoricalAwards(naicsCodes, 50);
    logger.info(`Generated ${historicalAwards.length} mock historical awards`);

    // Store opportunities in Firestore
    onProgress?.({ status: "running", message: "Storing opportunities in Firestore..." });
    const opportunitiesRef = collection(db, "sam_opportunities");

    for (const opp of opportunities) {
      try {
        // Compute similarity against historical awards
        const scoredAwards = historicalAwards.map((award) => {
          const score = computeSimilarityScore(
            {
              naics_code: opp.naics_code,
              psc_code: opp.psc_code,
              agency_name: opp.department_name,
              sub_agency_name: opp.sub_tier_name,
              description: opp.description,
            },
            {
              naics_code: award.naics_code,
              psc_code: award.psc_code,
              agency_name: award.agency_name,
              sub_agency_name: award.sub_agency_name,
              description: award.description,
            }
          );
          return { ...award, similarity_score: score, similarity_band: computeSimilarityBand(score) };
        })
          .filter((a) => a.similarity_score >= 0.1)
          .sort((a, b) => b.similarity_score - a.similarity_score)
          .slice(0, 5);

        results.similarity_matches += scoredAwards.length;

        // Store opportunity with similarity info
        const docRef = doc(opportunitiesRef, opp.id);
        await setDoc(docRef, {
          ...opp,
          similar_awards: scoredAwards,
          similarity_band: scoredAwards[0]?.similarity_band || null,
          similarity_score: scoredAwards[0]?.similarity_score || 0,
          similar_awards_count: scoredAwards.length,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        });

        results.opportunities_stored++;
      } catch (error) {
        logger.warn(`Failed to store opportunity ${opp.id}: ${error.message}`);
        results.errors.push({ id: opp.id, error: error.message });
      }
    }

    // Store historical awards
    onProgress?.({ status: "running", message: "Storing historical awards..." });
    const awardsRef = collection(db, "historical_awards");

    for (const award of historicalAwards) {
      try {
        const docId = `${award.piid}_${award.award_id}`.replace(/[/\\]/g, "_");
        const docRef = doc(awardsRef, docId);
        await setDoc(docRef, {
          ...award,
          updated_at: serverTimestamp(),
        });
      } catch (error) {
        logger.warn(`Failed to store award ${award.piid}: ${error.message}`);
      }
    }

    // Record ingest run
    const ingestRunsRef = collection(db, "ingest_runs");
    const runDoc = doc(ingestRunsRef);
    results.status = "completed";
    results.duration_ms = Date.now() - results.started_at.getTime();

    await setDoc(runDoc, {
      ...results,
      source: "demo-mock-data",
      completed_at: serverTimestamp(),
    });

    logger.info(`Demo ingest completed: ${results.opportunities_stored} opportunities stored`);
    onProgress?.({
      status: "completed",
      message: `Demo completed! Stored ${results.opportunities_stored} mock opportunities with ${results.similarity_matches} similarity matches.`,
    });

    return results;
  } catch (error) {
    logger.error(`Demo ingest failed: ${error.message}`);
    results.status = "failed";
    results.error = error.message;
    onProgress?.({
      status: "failed",
      message: `Demo failed: ${error.message}`,
    });
    throw error;
  }
}
